import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';

interface Achievement {
  id: string;
  category: string;
  title: string;
  description: string;
  icon: string;
  xp_reward: number;
  target_value: number;
  shareable?: boolean;
  slava_line?: string | null;
  hidden?: boolean;
}

const STORAGE_KEY = 'slava_seen_achievements';
const POLL_INTERVAL_MS = 30 * 1000; // 30 секунд
// Достижение старше этого срока — уже не новость. Его не показываем окном,
// а молча помечаем: оно остаётся на экране «Достижения», и поделиться можно оттуда.
const FRESH_WINDOW_MS = 72 * 60 * 60 * 1000;

/**
 * Следит за новыми достижениями и отдаёт ОДНО окно за раз.
 *
 * Было (до 05.10.2026): «уже показано» жило только в localStorage. У человека
 * с 20 накопленными достижениями память устройства разошлась с сервером, и окна
 * пошли одно за другим — закрыл «Не сейчас», через полсекунды следующее.
 *
 * Стало: истина на сервере (user_achievements.seen_at, миграция 056).
 * Три правила, каждое закрывает свой способ устроить поток окон:
 *  1. спрашиваем только открытое и ещё не показанное — смена телефона или
 *     очистка кэша старое не поднимает;
 *  2. старше 72 часов — не новость, помечаем молча (достижения, выданные задним
 *     числом пачкой, больше не всплывают никогда);
 *  3. свежих несколько — показываем одно, самое весомое, остальные помечаем молча.
 *
 * localStorage остался вторым рубежом: если отметка на сервер не дошла (нет сети),
 * на этом устройстве окно всё равно не повторится.
 */
export function useAchievementWatcher() {
  const { profile } = useUserStore();
  const [pendingAchievement, setPendingAchievement] = useState<Achievement | null>(null);
  const isShowingRef = useRef(false);
  const checkingRef = useRef(false);
  // Что уже показано или помечено в ЭТОЙ сессии — чтобы опрос раз в 30 секунд
  // не поднял то же самое, пока отметка летит на сервер.
  const handledRef = useRef<Set<string>>(new Set());

  function getLocalSeen(): Set<string> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  }

  function markSeen(ids: string[]) {
    if (!ids.length) return;
    ids.forEach((id) => handledRef.current.add(id));
    try {
      const seen = getLocalSeen();
      ids.forEach((id) => seen.add(id));
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...seen]));
    } catch (e) {
      console.warn('[achievement watcher] localStorage error:', e);
    }
    // supabase-js не бросает исключение, ошибка приходит полем — читаем её явно.
    supabase.rpc('mark_achievements_seen', { p_ids: ids }).then(({ error }) => {
      if (error) console.warn('[achievement watcher] mark seen error:', error);
    });
  }

  function dismissAchievement() {
    setPendingAchievement(null);
    isShowingRef.current = false;
  }

  async function checkForNewAchievements() {
    if (!profile?.id || checkingRef.current || isShowingRef.current) return;
    checkingRef.current = true;

    try {
      const { data, error } = await supabase
        .from('user_achievements')
        .select(`
          achievement_id,
          unlocked_at,
          achievements:achievement_id (
            id, category, title, description, icon, xp_reward, target_value, shareable, slava_line, hidden
          )
        `)
        .eq('user_id', profile.id)
        .not('unlocked_at', 'is', null)
        .is('seen_at', null)
        .order('unlocked_at', { ascending: false });

      if (error) {
        console.warn('[achievement watcher] query error:', error);
        return;
      }
      if (!data || data.length === 0) return;

      const localSeen = getLocalSeen();
      const now = Date.now();
      const silent: string[] = [];
      const fresh: { ach: Achievement; at: number }[] = [];

      for (const row of data as any[]) {
        const ach = row.achievements as Achievement | null;
        if (!ach) continue;
        const at = new Date(row.unlocked_at).getTime();
        if (ach.hidden || now - at > FRESH_WINDOW_MS || localSeen.has(ach.id) || handledRef.current.has(ach.id)) {
          // Скрытые шаги онбординга, старое и то, что это устройство уже показывало,
          // но отметка не дошла до сервера, — дотягиваем отметку без окна.
          silent.push(ach.id);
        } else {
          fresh.push({ ach, at });
        }
      }

      // Самое весомое из свежих: позднее по времени, при равенстве — с большей целью
      // (из «серия 21 / 40 / 50», выданных разом, покажем 50).
      fresh.sort((a, b) => (b.at - a.at) || (b.ach.target_value - a.ach.target_value));
      const [top, ...rest] = fresh;
      silent.push(...rest.map((f) => f.ach.id));

      if (top && !isShowingRef.current) {
        isShowingRef.current = true;
        // Помечаем при ПОКАЗЕ, а не при закрытии: окно может висеть сколько угодно.
        markSeen([top.ach.id, ...silent]);
        setPendingAchievement(top.ach);
      } else {
        markSeen(silent);
      }
    } catch (e) {
      console.warn('[achievement watcher] error:', e);
    } finally {
      checkingRef.current = false;
    }
  }

  useEffect(() => {
    if (!profile?.id) return;

    checkForNewAchievements();
    const interval = setInterval(checkForNewAchievements, POLL_INTERVAL_MS);

    // При возвращении в приложение — проверяем сразу
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') checkForNewAchievements();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [profile?.id]);

  return {
    pendingAchievement,
    dismissAchievement,
  };
}
